import { useEffect, useCallback } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { getContrastTextColor } from '@vibesites/tokens';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePropertyAddressUrl,
  usePropertyName,
  usePropertyPhone,
  usePropertyField,
  usePrimaryColor,
  useSecondaryColor,
} from '@vibesites/runtime/react';
import {
  EditableToken,
  EditableSlotGroup,
  EditableCollectionBinding,
  RichTextContent,
} from '@vibesites/runtime/react';
import {
  MultihubLogoMonoWhite,
  MultihubLogoMonoBlack,
  repliWhiteLogoUrl,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
} from '@vibesites/assets';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faGoogle, faInstagram, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import { faCommentDots } from '@fortawesome/free-solid-svg-icons';
// Local black variant of the Repli wordmark — `@vibesites/assets` only ships
// `repliWhiteLogoUrl`, so we keep a dark-fill copy for light primary backgrounds.
import repliBlackLogoUrl from '../assets/repli_black.svg';

// ============================================================================
// Contrast helpers
// ============================================================================

const TEXT_ON_PRIMARY_LIGHT = '#ffffff';
/** WCAG AA contrast ratio for normal-size text (legal links are ~10px). */
const MIN_ACCENT_CONTRAST_RATIO = 4.5;

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  let cleaned = hex.replace('#', '').trim();
  if (cleaned.length === 4) cleaned = cleaned.slice(0, 3);
  if (cleaned.length === 8) cleaned = cleaned.slice(0, 6);
  if (cleaned.length === 3) {
    return {
      r: parseInt(cleaned[0] + cleaned[0], 16),
      g: parseInt(cleaned[1] + cleaned[1], 16),
      b: parseInt(cleaned[2] + cleaned[2], 16),
    };
  }
  if (cleaned.length === 6) {
    return {
      r: parseInt(cleaned.slice(0, 2), 16),
      g: parseInt(cleaned.slice(2, 4), 16),
      b: parseInt(cleaned.slice(4, 6), 16),
    };
  }
  return null;
}

function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const [rs, gs, bs] = [rgb.r / 255, rgb.g / 255, rgb.b / 255].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );
  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/** WCAG contrast ratio between two hex colors. Returns 0 if either can't be parsed. */
function getContrastRatio(foreground: string, background: string): number {
  const fgLum = getRelativeLuminance(foreground);
  const bgLum = getRelativeLuminance(background);
  if (!hexToRgb(foreground) || !hexToRgb(background)) return 0;
  const lighter = Math.max(fgLum, bgLum);
  const darker = Math.min(fgLum, bgLum);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Prefer secondary as the accent on a primary background when it meets WCAG AA.
 * Otherwise fall back to white-on-dark / black-on-light via `getContrastTextColor`.
 */
function resolveAccentOnPrimary(
  secondary: string | null | undefined,
  primary: string | null | undefined
): string {
  const bg = primary || '#000000';
  if (secondary && getContrastRatio(secondary, bg) >= MIN_ACCENT_CONTRAST_RATIO) {
    return secondary;
  }
  return getContrastTextColor(bg);
}

// ============================================================================
// Types
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

/** Formats a phone string into XXX-XXX-XXXX to match the Roxanne demo footer. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * Split a full address so street (and suite) stay on line 1 and
 * "City, ST ZIP" move to line 2. Falls back to the first comma when the
 * trailing city/state/zip pattern is missing.
 */
function formatAddressLines(raw: string): { street: string; cityStateZip: string } | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const cityStateZipMatch = trimmed.match(
    /^(.*?),\s*([^,]+,\s*[A-Za-z]{2}\s+\d{5}(?:-\d{4})?)\s*$/
  );
  if (cityStateZipMatch) {
    return {
      street: cityStateZipMatch[1].trim(),
      cityStateZip: cityStateZipMatch[2].trim(),
    };
  }

  const comma = trimmed.indexOf(',');
  if (comma === -1) return null;
  return {
    street: trimmed.slice(0, comma).trim(),
    cityStateZip: trimmed.slice(comma + 1).trim(),
  };
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

interface GroupedHours {
  shortLabel: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const sorted = [...raw].sort((a, b) => {
    const ai = DAY_ORDER.indexOf(normalizeDay(a.day || ''));
    const bi = DAY_ORDER.indexOf(normalizeDay(b.day || ''));
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const groups: GroupedHours[] = [];

  for (const entry of sorted) {
    if (!hasValue(entry.day)) continue;

    const key = normalizeDay(entry.day);
    const dayShortLabel = DAY_SHORT_LABELS[key] || (entry.day || '').slice(0, 3);
    const isClosed = !hasValue(entry.open_time) && !hasValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_mono_white_logo',
      suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

// "Professionally Managed by" — the property-management company (Weidner).
// Ships an optional image override; when none is set we draw an inline
// wordmark fallback in the footer's on-primary text color.
const professionallyManagedByLogoDef: SlotGroupDefinition = {
  label: 'Professionally Managed By Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_company_mono_white_logo',
      suggestedFields: [
        'property_company_mono_white_logo',
        'property_company_logo',
        'property_company_mono_black_logo',
      ],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Weidner Apartment Homes' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.weidner.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Powered By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Multihub' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.multihub.io/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli Logo' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address Text',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, City, ST 00000',
    },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// Weidner-specific "Text Us" link. URL binds to the per-property
// `custom_text_us_url` custom field; the link is suppressed entirely when no
// URL is set so communities without an SMS landing page don't render a dead link.
const textUsLinkDef: SlotGroupDefinition = {
  label: 'Text Us Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Text Us' },
    url: { label: 'URL', type: 'url', defaultField: 'custom_text_us_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const facebookLinkDef: SlotGroupDefinition = {
  label: 'Facebook',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_facebook_url',
      suggestedFields: ['property_facebook_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const twitterLinkDef: SlotGroupDefinition = {
  label: 'Twitter / X',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_twitter_url',
      suggestedFields: ['property_twitter_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const instagramLinkDef: SlotGroupDefinition = {
  label: 'Instagram',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_instagram_url',
      suggestedFields: ['property_instagram_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const googleLinkDef: SlotGroupDefinition = {
  label: 'Google Business',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_google_business_url',
      suggestedFields: ['property_google_business_url'],
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// Weidner brand-program logos (Weidner Cares, Love Notes). Each ships a default
// company-level program logo and falls back to a small inline SVG wordmark
// drawn in the footer's on-primary text color so something always renders
// against the brand background even before an image is set in the editor.
const weidnerCaresLogoDef: SlotGroupDefinition = {
  label: 'Weidner Cares Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultValue: 'https://media.multihub.io/cdn-cgi/image/format=auto,w=1920/company/5726763346821120/019d44f3-3452-767e-8966-83b6939f057f.webp' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Weidner Cares' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.weidner.com/weidnercares' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const loveNotesLogoDef: SlotGroupDefinition = {
  label: 'Love Notes Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultValue: 'https://media.multihub.io/cdn-cgi/image/format=auto,w=1920/company/5726763346821120/019d44f3-3412-75a9-9aac-8f5678cb59df.webp' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Love Notes' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.weidner.com/lovenotes' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Inline SVG Fallback Logos
// Rendered when no image is set on the Weidner Cares / Love Notes slots. Drawn
// in currentColor so they inherit the footer's on-primary text color.
// ============================================================================

function WeidnerCaresLogoFallback({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 160 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M10 30L16 10L22 25L28 10L34 30" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <text x="42" y="24" fill="currentColor" fontFamily="sans-serif" fontSize="11" fontWeight="600" letterSpacing="0.5">WEIDNER</text>
      <text x="42" y="35" fill="currentColor" fontFamily="sans-serif" fontSize="9" fontWeight="400" letterSpacing="1.5">CARES</text>
    </svg>
  );
}

/** Weidner Apartment Homes wordmark fallback (used when no image is set on the
 *  "Professionally Managed by" slot). Drawn in currentColor. */
function WeidnerApartmentHomesLogo({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 168 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      {/* Small building emblem */}
      <path
        d="M8 34V16l10-8 10 8v18h-6v-9h-8v9H8z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
        fill="none"
      />
      <text x="36" y="24" fill="currentColor" fontFamily="serif" fontSize="15" fontWeight="700" letterSpacing="1">WEIDNER</text>
      <text x="37" y="36" fill="currentColor" fontFamily="sans-serif" fontSize="7.5" fontWeight="400" letterSpacing="2">APARTMENT HOMES</text>
    </svg>
  );
}

function LoveNotesLogoFallback({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 140 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M16 14C16 10 20 8 22 10C24 8 28 10 28 14C28 20 22 26 22 26C22 26 16 20 16 14Z" stroke="currentColor" strokeWidth="1.5" fill="none" />
      <text x="36" y="22" fill="currentColor" fontFamily="sans-serif" fontSize="11" fontWeight="600" letterSpacing="0.5">LOVE</text>
      <text x="36" y="33" fill="currentColor" fontFamily="sans-serif" fontSize="9" fontWeight="400" letterSpacing="1.5">NOTES</text>
    </svg>
  );
}

// Plain monochrome social glyphs (no filled circle) that inherit the footer's
// on-primary text color, matching the reference footer.
const socialIconClasses =
  'inline-flex items-center justify-center w-6 h-6 text-primary-foreground hover:opacity-70 transition-opacity';

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const phoneToken = useContentToken('footer.phone');
  const trackedPhone = usePropertyPhone();
  const phone = phoneToken || trackedPhone || '123-456-7890';
  const propertyName = usePropertyName();
  const primaryColor = usePrimaryColor();
  const secondaryColor = useSecondaryColor();

  // Body / icons / credits use the auto-paired text-on-primary token.
  // Accent labels (Contact, Office Hours, legal links) prefer secondary when
  // it clears WCAG AA against primary; otherwise white or black.
  const accentColor = resolveAccentOnPrimary(secondaryColor, primaryColor);
  const textOnPrimary = getContrastTextColor(primaryColor || '#000000');
  const useWhiteOnPrimary = textOnPrimary === TEXT_ON_PRIMARY_LIGHT;

  const runtimeLogoSlotDef: SlotGroupDefinition = useWhiteOnPrimary
    ? logoSlotDef
    : {
        ...logoSlotDef,
        fields: {
          ...logoSlotDef.fields,
          image: {
            ...logoSlotDef.fields.image,
            defaultField: 'property_mono_black_logo',
          },
        },
      };

  const runtimeProfessionallyManagedByLogoDef: SlotGroupDefinition = useWhiteOnPrimary
    ? professionallyManagedByLogoDef
    : {
        ...professionallyManagedByLogoDef,
        fields: {
          ...professionallyManagedByLogoDef.fields,
          image: {
            ...professionallyManagedByLogoDef.fields.image,
            defaultField: 'property_company_mono_black_logo',
          },
        },
      };

  const logo = useSlotGroup('footer.logo', runtimeLogoSlotDef);
  const professionallyManagedByLogo = useSlotGroup(
    'footer.professionally_managed_by_logo',
    runtimeProfessionallyManagedByLogoDef
  );
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  const propertyAddressUrl = usePropertyAddressUrl();
  const textUsLink = useSlotGroup('footer.text_us_link', textUsLinkDef);
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);
  const facebook = useSlotGroup('footer.facebook_link', facebookLinkDef);
  const twitter = useSlotGroup('footer.twitter_link', twitterLinkDef);
  const instagram = useSlotGroup('footer.instagram_link', instagramLinkDef);
  const google = useSlotGroup('footer.google_link', googleLinkDef);

  const weidnerCaresLogo = useSlotGroup('footer.weidner_cares_logo', weidnerCaresLogoDef);
  const loveNotesLogo = useSlotGroup('footer.love_notes_logo', loveNotesLogoDef);

  // CORT furnished-living CTA — only rendered when the property has a CORT lead
  // form ID configured. Clicking opens the CORT modal injected at runtime by
  // the external CORT script (window.Cort_modal).
  const cortButtonText = useContentToken('footer.cort_button_text');
  const cortLeadFormId = (usePropertyField('custom_cort_lead_form_id') as string) || '';
  const openCortModal = useCallback(() => {
    const cort = typeof window !== 'undefined'
      ? (window as unknown as { Cort_modal?: { open?: () => void } }).Cort_modal
      : undefined;
    cort?.open?.();
  }, []);

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const groupedHours = officeHours && officeHours.length > 0
    ? groupOfficeHours(officeHours)
    : [];

  const hasSocial = !!(facebook.url || twitter.url || instagram.url || google.url);

  // Weidner-only: only render the "Text Us" link when the property has an SMS
  // landing URL configured (custom_text_us_url), so we never show a dead link.
  const showTextUsLink = hasValue(textUsLink.url);

  const addressText = addressLink.text || '123 Main Street, City, ST 00000';
  const addressLines = formatAddressLines(addressText);

  // ─── RentCafe chat launcher z-index pin ─────────────────────────────────────
  // The vendor injects the launcher after load and sets an inline z-index.
  // `!important` is required so that inline style loses. 120 sits just above
  // the open header (z-index 110) without covering the rest of the page.
  // Only the real launcher is pinned: the chat wrap, its controls, and the
  // webchat toggle. A fixed ancestor is pinned only when it is that wrap.
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const LAUNCHER_SELECTOR =
      '.rcc-app-cta-wrap, .rcc-app-controls, #conversations-webchat-toggle';
    const CHAT_WRAP_SELECTOR = '.rcc-app-cta-wrap';
    const Z = '120';

    function outermostFixedAncestor(el: Element): HTMLElement | null {
      let cur: Element | null = el;
      let last: HTMLElement | null = null;
      while (cur && cur !== document.body && cur instanceof HTMLElement) {
        const pos = getComputedStyle(cur).position;
        if (pos === 'fixed') last = cur;
        cur = cur.parentElement;
      }
      return last;
    }

    function pin(el: HTMLElement) {
      el.style.setProperty('z-index', Z, 'important');
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      if (!el.matches(LAUNCHER_SELECTOR)) return;
      pin(el);
      const fixedAncestor = outermostFixedAncestor(el);
      if (
        fixedAncestor &&
        fixedAncestor !== el &&
        fixedAncestor.matches(CHAT_WRAP_SELECTOR)
      ) {
        pin(fixedAncestor);
      }
    }

    function scanSubtree(root: ParentNode) {
      if (root instanceof Element) processCandidate(root);
      for (const candidate of Array.from(root.querySelectorAll(LAUNCHER_SELECTOR))) {
        processCandidate(candidate);
      }
    }

    scanSubtree(document.body);

    let scheduled = false;
    const observer = new MutationObserver((records) => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['id', 'class', 'style'],
    });

    return () => observer.disconnect();
  }, []);

  return (
    <footer
      className="text-primary-foreground"
      style={{ backgroundColor: 'var(--vibe-color-primary)' }}
    >
      {/* ---- Main Footer: 4 columns ---- */}
      <div className="max-w-[1290px] mx-auto px-6 sm:px-10 lg:px-12 pt-12 pb-10 md:pt-16 md:pb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12">

          {/* Column 1: Logo + copyright + socials */}
          <div className="flex flex-col items-center text-center gap-4">
            <EditableSlotGroup groupPath="footer.logo" definition={runtimeLogoSlotDef}>
              <a href="/" className="block">
                {logo.image ? (
                  <VibesitesImage
                    url={logo.image}
                    alt={logo.alt || 'Community Logo'}
                    className="h-auto w-auto max-h-[185px] max-w-[185px] object-contain"
                    sizes="185px"
                  />
                ) : (
                  <div
                    className="font-heading text-3xl"
                    style={{ color: accentColor }}
                  >
                    {propertyName || 'Our Community'}
                  </div>
                )}
              </a>
            </EditableSlotGroup>

            <p className="font-body text-[11px] leading-relaxed text-primary-foreground/90 max-w-[240px]">
              © {new Date().getFullYear()} All Rights Reserved
              {propertyName ? `. ${propertyName}` : ''}
            </p>

            {hasSocial ? (
              <div className="flex items-center justify-center gap-3">
                {facebook.url && (
                  <EditableSlotGroup groupPath="footer.facebook_link" definition={facebookLinkDef}>
                    <a
                      href={facebook.url}
                      target={facebook._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="Facebook"
                      className={socialIconClasses}
                    >
                      <FontAwesomeIcon icon={faFacebook} className="w-4 h-4" />
                    </a>
                  </EditableSlotGroup>
                )}
                {twitter.url && (
                  <EditableSlotGroup groupPath="footer.twitter_link" definition={twitterLinkDef}>
                    <a
                      href={twitter.url}
                      target={twitter._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="Twitter"
                      className={socialIconClasses}
                    >
                      <FontAwesomeIcon icon={faXTwitter} className="w-3.5 h-3.5" />
                    </a>
                  </EditableSlotGroup>
                )}
                {instagram.url && (
                  <EditableSlotGroup groupPath="footer.instagram_link" definition={instagramLinkDef}>
                    <a
                      href={instagram.url}
                      target={instagram._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="Instagram"
                      className={socialIconClasses}
                    >
                      <FontAwesomeIcon icon={faInstagram} className="w-4 h-4" />
                    </a>
                  </EditableSlotGroup>
                )}
                {google.url && (
                  <EditableSlotGroup groupPath="footer.google_link" definition={googleLinkDef}>
                    <a
                      href={google.url}
                      target={google._target || '_blank'}
                      rel="noopener noreferrer"
                      aria-label="Google Business"
                      className={socialIconClasses}
                    >
                      <FontAwesomeIcon icon={faGoogle} className="w-4 h-4" />
                    </a>
                  </EditableSlotGroup>
                )}
              </div>
            ) : null}
          </div>

          {/* Column 2: Contact */}
          <div className="flex flex-col items-center text-center gap-3">
            <RichTextContent
              path="footer.contact_label"
              defaultValue="<p>Contact Us</p>"
              as="p"
              className="font-body text-lg font-semibold leading-none text-primary-foreground mb-1"
            />
            {showTextUsLink && (
              <EditableSlotGroup groupPath="footer.text_us_link" definition={textUsLinkDef}>
                <a
                  href={textUsLink.url || '#'}
                  target={textUsLink._target || '_blank'}
                  rel="noopener noreferrer"
                  aria-label={textUsLink.text || 'Text Us'}
                  className="inline-flex items-center gap-1.5 font-body text-[13px] text-primary-foreground hover:opacity-70 transition-opacity"
                >
                  <FontAwesomeIcon icon={faCommentDots} className="w-3.5 h-3.5" />
                  <span>{textUsLink.text || 'Text Us'}</span>
                </a>
              </EditableSlotGroup>
            )}
            <a
              href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
              className="dniphone dniphonehref font-body text-[13px] text-primary-foreground hover:opacity-70 transition-opacity"
            >
              <EditableToken
                path="footer.phone"
              >
                {formatPhone(phone)}
              </EditableToken>
            </a>
            <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
              <a
                href={addressLink.url || propertyAddressUrl || '#'}
                target={addressLink._target || '_blank'}
                rel="noopener noreferrer"
                className="font-body text-[13px] text-primary-foreground hover:opacity-70 transition-opacity block max-w-[220px]"
              >
                {addressLines ? (
                  <>
                    <span className="block">{addressLines.street}</span>
                    <span className="block">{addressLines.cityStateZip}</span>
                  </>
                ) : (
                  addressText
                )}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Column 3: Office Hours */}
          <div className="flex flex-col items-center text-center">
            <RichTextContent
              path="footer.office_hours_label"
              defaultValue="<p>Office Hours</p>"
              as="p"
              className="font-body text-lg font-semibold leading-none mb-3 text-primary-foreground"
            />
            <EditableCollectionBinding bindingPath="footer.office_hours" label="Office Hours">
              {groupedHours.length > 0 ? (
                <dl className="space-y-2 w-full max-w-[260px]">
                  {groupedHours.map((group, index) => (
                    <div
                      key={index}
                      className="flex justify-between gap-4 font-body text-[13px] text-primary-foreground"
                    >
                      <dt>{group.shortLabel}</dt>
                      <dd className="text-right">{group.hours}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="font-body text-[13px] text-primary-foreground/40">
                  Office hours will be displayed here.
                </p>
              )}
            </EditableCollectionBinding>
          </div>

        </div>

        {/* ---- Weidner programs (Weidner Cares & Love Notes) + CORT CTA ----
            Sits inside the main footer container, left-aligned under the columns.
            The two program logos always render (image or inline-SVG fallback);
            the CORT CTA only appears when the property has a CORT lead-form ID. */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8">
          <div className="flex flex-row items-center justify-center gap-6 sm:gap-8">
          <EditableSlotGroup groupPath="footer.weidner_cares_logo" definition={weidnerCaresLogoDef}>
            <a
              href={weidnerCaresLogo.url || 'https://www.weidner.com/weidnercares'}
              target={weidnerCaresLogo._target || '_blank'}
              rel="noopener noreferrer"
              aria-label={weidnerCaresLogo.alt || 'Weidner Cares'}
              className="inline-flex text-primary-foreground hover:opacity-70 transition-opacity"
            >
              {weidnerCaresLogo.image ? (
                <VibesitesImage
                  url={weidnerCaresLogo.image}
                  alt={weidnerCaresLogo.alt || 'Weidner Cares'}
                  height={40}
                  sizes="120px"
                  className="h-10 w-auto max-w-[120px] object-contain"
                />
              ) : (
                <WeidnerCaresLogoFallback className="h-10 w-auto" />
              )}
            </a>
          </EditableSlotGroup>

          <EditableSlotGroup groupPath="footer.love_notes_logo" definition={loveNotesLogoDef}>
            <a
              href={loveNotesLogo.url || 'https://www.weidner.com/lovenotes'}
              target={loveNotesLogo._target || '_blank'}
              rel="noopener noreferrer"
              aria-label={loveNotesLogo.alt || 'Love Notes'}
              className="inline-flex text-primary-foreground hover:opacity-70 transition-opacity"
            >
              {loveNotesLogo.image ? (
                <VibesitesImage
                  url={loveNotesLogo.image}
                  alt={loveNotesLogo.alt || 'Love Notes'}
                  height={40}
                  sizes="120px"
                  className="h-10 w-auto max-w-[120px] object-contain"
                />
              ) : (
                <LoveNotesLogoFallback className="h-10 w-auto" />
              )}
            </a>
          </EditableSlotGroup>
          </div>

          {cortLeadFormId ? (
            <button
              type="button"
              onClick={openCortModal}
              className="footer-cort-cta group/cort relative inline-flex items-center gap-2 px-6 py-2.5 text-xs font-bold tracking-[0.12em] uppercase overflow-hidden transition-all duration-300"
            >
              <EditableToken path="footer.cort_button_text" as="span" className="relative z-10">
                {cortButtonText || 'Furnish Your Home with CORT'}
              </EditableToken>
              <span
                className="relative z-10 text-base leading-none transition-transform duration-300 group-hover/cort:translate-x-0.5"
                aria-hidden="true"
              >
                ›
              </span>
            </button>
          ) : null}
        </div>
      </div>

      {/* ---- Divider ---- */}
      {/* Slash opacity on hex CSS vars is unreliable, so pick the border from
          the same white/dark branch used for logos. */}
      <div className="max-w-[1290px] mx-auto px-6 sm:px-10 lg:px-12">
        <hr className={`border-t ${useWhiteOnPrimary ? 'border-white/80' : 'border-[#1a1a1a]/80'}`} />
      </div>

      {/* ---- Compliance icons + legal links (centered band) ---- */}
      <div className="max-w-[1290px] mx-auto px-6 sm:px-10 lg:px-12 pt-8 pb-2">
        <div className="flex items-center justify-center gap-6 text-primary-foreground">
          <WheelchairIcon size={30} color="currentColor" className="block" aria-label="Accessible" />
          <FairHousingIcon size={30} color="currentColor" className="block" aria-label="Equal Housing Opportunity" />
          <PetsAllowedIcon size={30} color="currentColor" className="block" aria-label="Pets Allowed" />
        </div>
        <nav
          className="mt-4 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 font-body text-[12px] text-primary-foreground"
          aria-label="Legal links"
        >
          <EditableSlotGroup groupPath="footer.accessibility_link" definition={accessibilityLinkDef}>
            <a
              href={accessibilityLink.url || '/accessibility'}
              target={accessibilityLink._target || '_self'}
              className="hover:opacity-70 transition-opacity"
            >
              {accessibilityLink.text || 'Accessibility'}
            </a>
          </EditableSlotGroup>
          <span aria-hidden="true" className="opacity-60">·</span>
          <EditableSlotGroup groupPath="footer.privacy_policy_link" definition={privacyPolicyLinkDef}>
            <a
              href={privacyPolicyLink.url || '/privacy-policy'}
              target={privacyPolicyLink._target || '_self'}
              className="hover:opacity-70 transition-opacity"
            >
              {privacyPolicyLink.text || 'Privacy Policy'}
            </a>
          </EditableSlotGroup>
          <span aria-hidden="true" className="opacity-60">·</span>
          <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
            <a
              href={sitemapLink.url || '/sitemap'}
              target={sitemapLink._target || '_self'}
              className="hover:opacity-70 transition-opacity"
            >
              {sitemapLink.text || 'Sitemap'}
            </a>
          </EditableSlotGroup>
        </nav>
      </div>

      {/* ---- Bottom bar: Professionally Managed by | Powered by | Website Design by ---- */}
      <div className="max-w-[1290px] mx-auto px-6 sm:px-10 lg:px-12 pt-6 pb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-8 font-body text-xs text-primary-foreground">
          {/* Professionally Managed by (Weidner) */}
          <EditableSlotGroup
            groupPath="footer.professionally_managed_by_logo"
            definition={runtimeProfessionallyManagedByLogoDef}
            className="flex justify-center md:justify-start"
          >
            <a
              href={professionallyManagedByLogo.url || 'https://www.weidner.com/'}
              target={professionallyManagedByLogo._target || '_blank'}
              rel="noopener noreferrer"
              className="flex flex-col items-center md:items-start gap-1.5 opacity-90 hover:opacity-100 transition-opacity"
            >
              <RichTextContent
                path="footer.professionally_managed_by_text"
                defaultValue="<p>Professionally Managed by</p>"
                as="span"
                className="text-[11px] text-primary-foreground"
              />
              {professionallyManagedByLogo.image ? (
                <VibesitesImage
                  url={professionallyManagedByLogo.image}
                  alt={professionallyManagedByLogo.alt || 'Weidner Apartment Homes'}
                  height={28}
                  sizes="160px"
                  className="h-7 w-auto max-w-[160px] object-contain"
                />
              ) : (
                <WeidnerApartmentHomesLogo className="h-7 w-auto" />
              )}
            </a>
          </EditableSlotGroup>

          {/* Powered by (Multihub) */}
          <EditableSlotGroup
            groupPath="footer.managed_by_logo"
            definition={managedByLogoDef}
            className="flex justify-center"
          >
            <a
              href={managedByLogo.url || 'https://www.multihub.io/'}
              target={managedByLogo._target || '_blank'}
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1 opacity-90 hover:opacity-100 transition-opacity"
            >
              <RichTextContent
                path="footer.managed_by_text"
                defaultValue="<p>Powered by</p>"
                as="span"
                className="text-[11px] text-primary-foreground"
              />
              {useWhiteOnPrimary ? (
                <MultihubLogoMonoWhite height={18} alt={managedByLogo.alt || 'Multihub'} />
              ) : (
                <MultihubLogoMonoBlack height={18} alt={managedByLogo.alt || 'Multihub'} />
              )}
            </a>
          </EditableSlotGroup>

          {/* Website Design by (Repli) */}
          <EditableSlotGroup
            groupPath="footer.design_by_logo"
            definition={designByLogoDef}
            className="flex justify-center md:justify-end"
          >
            <a
              href={designByLogo.url || 'https://www.repli360.com/'}
              target={designByLogo._target || '_blank'}
              rel="noopener noreferrer"
              className="flex flex-col items-center md:items-end gap-1 opacity-90 hover:opacity-100 transition-opacity"
            >
              <RichTextContent
                path="footer.design_by_text"
                defaultValue="<p>Website Design by</p>"
                as="span"
                className="text-[11px] text-primary-foreground"
              />
              <VibesitesImage
                url={useWhiteOnPrimary ? repliWhiteLogoUrl : repliBlackLogoUrl}
                alt={designByLogo.alt || 'Repli Logo'}
                height={16}
                className="h-4 w-auto"
                sizes="80px"
                priority
              />
            </a>
          </EditableSlotGroup>
        </div>

        {/* Identity verification disclaimer */}
        <RichTextContent
          path="footer.identity_verification_text"
          defaultValue="<p>We participate in identity verification of all applicants.</p>"
          as="p"
          className="mt-6 text-center font-body text-[12px] text-primary-foreground"
        />
      </div>

      <style>{`
        /* CORT CTA — mirrors the theme's PageIntro CTA (filled secondary,
           square corners, uppercase). Hover lifts + slightly darkens instead
           of swapping to primary, since the footer already sits on primary and
           a primary fill would blend into the background. */
        .footer-cort-cta {
          background: var(--vibe-color-secondary);
          border: none;
          border-radius: 2px;
          color: var(--vibe-color-text-on-secondary, #fff);
          box-shadow: none;
        }
        .footer-cort-cta:hover {
          filter: brightness(0.92);
          transform: translateY(-1px);
        }
        .footer-cort-cta:active {
          transform: translateY(0);
        }
        .rcc-app-cta-wrap,
        .rcc-app-controls,
        #conversations-webchat-toggle {
          z-index: 120 !important;
        }
      `}</style>
    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    'footer.contact_label': {
      label: 'Contact Label',
      type: 'richtext',
      defaultValue: '<p>Contact Us</p>',
    },
    'footer.office_hours_label': {
      label: 'Office Hours Label',
      type: 'richtext',
      defaultValue: '<p>Office Hours</p>',
    },
    'footer.professionally_managed_by_text': {
      label: 'Professionally Managed By Text',
      type: 'richtext',
      defaultValue: '<p>Professionally Managed by</p>',
    },
    'footer.managed_by_text': {
      label: 'Powered By Text',
      type: 'richtext',
      defaultValue: '<p>Powered by</p>',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'richtext',
      defaultValue: '<p>Website Design by</p>',
    },
    'footer.identity_verification_text': {
      label: 'Identity Verification Disclaimer',
      type: 'richtext',
      defaultValue: '<p>We participate in identity verification of all applicants.</p>',
    },
    'footer.cort_button_text': {
      label: 'CORT Button Text',
      type: 'text',
      defaultValue: 'Furnish Your Home with CORT',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.professionally_managed_by_logo': professionallyManagedByLogoDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.address': addressLinkDef,
    'footer.text_us_link': textUsLinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
    'footer.facebook_link': facebookLinkDef,
    'footer.twitter_link': twitterLinkDef,
    'footer.instagram_link': instagramLinkDef,
    'footer.google_link': googleLinkDef,
    'footer.weidner_cares_logo': weidnerCaresLogoDef,
    'footer.love_notes_logo': loveNotesLogoDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Footer;
